// eslint-disable-next-line
import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Modal } from "antd"
import { Formik } from "formik"
import { Form, Input } from "formik-antd"
import * as yup from "yup"
import styled, { keyframes } from "styled-components"

import { footerText } from "util/config"
import { AppState } from "ReducerState"
import { SEND_EMAIL_COMPLETE } from "constants/ConstantsVariable"
import { requestResetUser, resetProcessStatus } from "appRedux/actions/Auth"

/* ── Animations ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ── Page background ── */
const PageWrap = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: #f4f7f9;
  position: relative;
  overflow: hidden;
  padding: 32px 16px;
`

/* ── Card ── */
const Card = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
  padding: 40px;
  animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`

/* ── Logo row ── */
const LogoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`

const LogoImg = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`

const BrandName = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #111827;
`

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: #f3f4f6;
  margin-bottom: 24px;
`

const WelcomeTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  text-align: center;
  margin: 0 0 6px;
`

const WelcomeSubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  margin: 0 0 28px;
`

/* ── Form body ── */
const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`

const FieldWrap = styled.div`
  margin-bottom: 16px;

  .ant-input {
    height: 42px;
    border-radius: 6px;
    background: #f4f5f7;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
  }

  .ant-input:hover {
    border-color: #cbd5e1;
  }

  .ant-input:focus {
    background: #eff6ff;
    border-color: #60a5fa;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`

export const DivBlockWarning = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #ef4444;
  font-weight: 400;
  margin-top: 4px;
`

const SuccessBox = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #0369a1;
  background: #e0f2fe;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  margin-bottom: 24px;
  line-height: 1.6;
`

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`

const SubmitBtn = styled(Button)`
  flex: 1;
  height: 42px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  background: #3b82f6 !important;
  border-color: #3b82f6 !important;
  box-shadow: none;

  &:hover {
    background: #2563eb !important;
    border-color: #2563eb !important;
  }
`

const BackBtn = styled(Button)`
  flex: 1;
  height: 42px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
`

const FooterBlock = styled.div`
  margin-top: 32px;
  text-align: center;
`

const FooterLine = styled.div`
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.7;
`

interface ActivateType {
  user?: string;
}

const RequestResetUser = ({ match, history }: any) => {
  const [isEmailSent, setIsEmailSent] = useState(false)
  const action = match.params.action
  const isActivate = action === "activate"
  const [initialData] = useState<ActivateType>({ user: "" })

  const { auth } = useSelector((state: AppState) => state)
  const { loader, process: authProcess } = auth
  const dispatch = useDispatch()

  useEffect(() => {
    if (authProcess.action === "userRequestReset") {
      const resMsg = authProcess.message || ""
      if (authProcess.status === true && authProcess.res_code === "0") {
        setIsEmailSent(true)
      } else {
        Modal.error({ title: 'Error', content: resMsg })
      }
      setTimeout(() => { dispatch(resetProcessStatus()) }, 500)
    }
  }, [dispatch, authProcess])

  const handleSubmit = (values: any) => {
    const userInfo = isActivate
      ? { action_type: "activate-user", user_name: values.user, email: '' }
      : { action_type: "reset-password", user_name: '', email: values.user }
    dispatch(requestResetUser(userInfo))
  }

  const onBack = () => {
    setIsEmailSent(false)
    history.replace("/signin")
  }

  const schema = yup.object({
    user: yup
      .string()
      .required(isActivate ? "Please input user name" : "Please input Email")
      .matches(RegExp("^([a-zA-Z0-9_.-])+@([a-zA-Z0-9-]+)"), "Please enter a valid format"),
  })

  return (
    <PageWrap>
      <Card>
        <LogoRow>
          <LogoImg src={`${process.env.PUBLIC_URL}/icon-512x512.png`} alt="FROMAS" />
          <BrandName>FROMAS</BrandName>
        </LogoRow>
        
        <DividerLine />

        <WelcomeTitle>{isActivate ? "Activate Account" : "Forgot Password"}</WelcomeTitle>
        <WelcomeSubtitle>
          {isActivate
            ? "Enter your username to receive an activation link"
            : "Enter your email to receive a password reset link"}
        </WelcomeSubtitle>

        <Formik
          enableReinitialize
          initialValues={initialData}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form layout="vertical">
              {isEmailSent ? (
                <>
                  <SuccessBox>{SEND_EMAIL_COMPLETE}</SuccessBox>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <BackBtn type="default" onClick={onBack} style={{ width: '100%' }}>
                      Back to Sign in
                    </BackBtn>
                  </div>
                </>
              ) : (
                <>
                  <FieldWrap>
                    <FieldLabel>{isActivate ? "Username" : "Email"}</FieldLabel>
                    <Input
                      name="user"
                      type="text"
                      placeholder={
                        isActivate
                          ? "your_username@property"
                          : "your_email@example.com"
                      }
                    />
                    {errors?.user && touched.user && (
                      <DivBlockWarning>{errors.user as string}</DivBlockWarning>
                    )}
                  </FieldWrap>

                  <ActionRow>
                    <BackBtn onClick={onBack}>
                      Back
                    </BackBtn>
                    <SubmitBtn type="primary" htmlType="submit" loading={loader}>
                      Submit
                    </SubmitBtn>
                  </ActionRow>
                </>
              )}
            </Form>
          )}
        </Formik>

        <FooterBlock>
          <FooterLine>Version: {process.env.REACT_APP_VERSION}</FooterLine>
          <FooterLine>{footerText.copyright}</FooterLine>
        </FooterBlock>
      </Card>
    </PageWrap>
  )
}

export default RequestResetUser

